import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute, NavigationEnd, Router, Event } from '@angular/router';
import { combineLatest, filter, Subscription } from 'rxjs';
import { AnalyticsService, LanguageService, SEOService, translate, UserService } from '@box-core/services';
import { NavItem, SeoOptions } from '@box-types';
import { filterValidNavItemsForUser, removeLanguageNavItem } from '@box/utils';

const MOBILE_BREAKPOINT = '(max-width: 576px)';

@Component({
  selector: 'account-submenu-page',
  templateUrl: './account-submenu.page.html',
  styleUrls: ['./account-submenu.page.scss']
})
export class AccountSubmenuPage implements OnInit, OnDestroy {
  public hideContainer: boolean;
  public accountSubmenuItems: NavItem[];
  public title: string;

  private breakPointObserverSubscription: Subscription;

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private changeDetectorRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private seoService: SEOService,
    private userService: UserService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.setMetaTags();
    this.setContainerHide();
    this.setBreakPointObserverSubscription();
    this.setNavItems();
    this.title = translate(this.activatedRoute.snapshot.data.breadcrumbName);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.breakPointObserverSubscription?.unsubscribe();
  }

  public onSubmenuItemClick(link: string): void {
    if (link === '/account/settings/language') {
      this.analyticsService.addGACustomEvent('click_language_change_profile', {});
    }
    if (link === '/account/settings/dark-mode') this.analyticsService.addGACustomEvent('click_dark_theme', {});
  }

  private setBreakPointObserverSubscription(): void {
    const routerObservable = this.router.events.pipe(filter((event: Event) => event instanceof NavigationEnd));
    combineLatest([routerObservable, this.breakpointObserver.observe([MOBILE_BREAKPOINT])]).subscribe(() => {
      this.setContainerHide();
    });
  }

  private setContainerHide(): void {
    const settingsCategoryPathUrl = this.router.url.endsWith('settings');
    const personalDataCategoryPathUrl = this.router.url.endsWith('personal-data');
    const termsAndPoliciesCategoryPathUrl = this.router.url.endsWith('terms');
    const isCategoryPathUrl = settingsCategoryPathUrl || personalDataCategoryPathUrl || termsAndPoliciesCategoryPathUrl;
    const isSubCategoryPathUrl = isCategoryPathUrl ? false : true;
    const isSmallScreen = this.breakpointObserver.isMatched(MOBILE_BREAKPOINT);
    this.hideContainer = isSubCategoryPathUrl && isSmallScreen;
    this.changeDetectorRef.detectChanges();
  }

  private setNavItems() {
    const isGuest = this.userService.isGuest;
    const validNavItemsForUser = filterValidNavItemsForUser(this.activatedRoute.snapshot.data.navItems, isGuest);
    const areThereMultipleLanguages = this.languageService.areThereMultipleLanguages();
    this.accountSubmenuItems = areThereMultipleLanguages
      ? validNavItemsForUser
      : removeLanguageNavItem(validNavItemsForUser);
  }

  private setMetaTags(): void {
    const options: SeoOptions = { title: this.activatedRoute.snapshot.data.breadcrumbName, url: this.router.url };
    this.seoService.setTags(options);
  }
}
